// ============================================================================
// DIRECT GRAPHQL HOOKS FOR EMPLOYEES (app_users)
// ----------------------------------------------------------------------------
// Mirrors the pattern used by ticket-hooks and task GraphQL hooks.
// ============================================================================

import { useCallback, useMemo } from "react";
import { useQuery, useMutation } from "@apollo/client/react";
import { gql } from "@apollo/client";
import type { EntityHooks } from "@/components/shared/UniversalCRMTable/types/shared";
import {
  GetUserssDocument,
  CreateUsersDocument,
  UpdateUsersDocument,
  DeleteUsersDocument,
  GetUserssQuery,
  CreateUsersMutation,
  UpdateUsersMutation,
  DeleteUsersMutation,
  App_Users,
  App_UsersInsertInput,
  App_UsersUpdateInput,
} from "@/src/gql/graphql";
import { usePaginatedCollection } from "@/lib/hooks/api/shared/use-paginated-collection";
import {
  EMPTY_INFINITE_RESULT,
  useStableMutationResult,
} from "@/lib/hooks/api/shared/stable-entity-hook-results";

/** Module-level so `usePaginatedCollection`'s result memo has stable deps. */
const selectEmployeesConnection = (data: GetUserssQuery) => data.app_usersCollection;
const mapEmployeeNode = (node: unknown): App_Users => node as App_Users;

export type Employee = {
  id: string;
  email: string;
  first_name?: string | null;
  last_name?: string | null;
  role_id?: string | null;
  is_active?: boolean | null;
  department?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
};

const GET_EMPLOYEE_DETAIL_LITE = gql`
  query GetEmployeeDetailLite($id: UUID!) {
    app_usersCollection(filter: { id: { eq: $id } }) {
      edges {
        node {
          id
          email
          first_name
          last_name
          role_id
          is_active
          created_at
          updated_at
        }
      }
    }
  }
`;

type GetEmployeeDetailLiteQuery = {
  app_usersCollection: {
    edges: Array<{
      node: App_Users | null;
    } | null> | null;
  } | null;
};

/**
 * Employee (app_users) entity hooks.
 *
 * Allocated ONCE at module scope. This used to be `useMemo(…, [])` inside the
 * factory, which was stable but made the factory a HOOK — and
 * `getGraphQLHooks(entityName)` dispatches to one of several factories by
 * string key, so a changed entity name would have shifted hook order. A module
 * constant is stable AND hook-free. See `contacts-graphql.ts`.
 */
const EMPLOYEE_ENTITY_HOOKS: EntityHooks<App_Users> = {
  queryDocument: GetUserssDocument,
  usePaginated: (params?: Record<string, unknown>) =>
    // Canonical list-query path (cache-and-network) — see use-paginated-collection.ts.
    usePaginatedCollection(
      GetUserssDocument,
      params,
      selectEmployeesConnection,
      mapEmployeeNode
    ),
  useInfinite: () => EMPTY_INFINITE_RESULT,

  useSingle: (id: string, options?: { enabled?: boolean }) => {
    const result = useQuery<GetEmployeeDetailLiteQuery>(GET_EMPLOYEE_DETAIL_LITE, {
      variables: { id },
      errorPolicy: "all",
      skip: !id,
      ...options,
    });

    return useMemo(
      () => ({
        ...result,
        data: result.data?.app_usersCollection?.edges?.[0]?.node || undefined,
        isLoading: result.loading,
        error: result.error || null,
        refetch: result.refetch,
      }),
      [result]
    );
  },

  useUpdate: () => {
    const [mutate, result] = useMutation<UpdateUsersMutation>(UpdateUsersDocument);

    const mutateAsync = useCallback(
      async ({ id, ...input }: { id: string } & App_UsersUpdateInput) => {
        const { __typename: _typename, ...cleanInput } = input as App_UsersUpdateInput & {
          __typename?: unknown;
        };

        const { data, error } = await mutate({
          variables: { id, input: cleanInput },
          errorPolicy: "all",
          refetchQueries: ["GetUserss", "GetUsers"],
          awaitRefetchQueries: true,
        });

        if (error) throw new Error(`GraphQL error: ${error.message}`);
        const updated = data?.updateapp_usersCollection?.records?.[0];
        if (!updated) throw new Error("Failed to update employee");
        return updated as App_Users;
      },
      [mutate]
    );

    return useStableMutationResult(mutateAsync, result.loading);
  },

  useDelete: () => {
    const [mutate, result] = useMutation<DeleteUsersMutation>(DeleteUsersDocument);

    const mutateAsync = useCallback(
      async (id: string) => {
        const { data, error } = await mutate({
          variables: { id },
          errorPolicy: "all",
          refetchQueries: ["GetUserss"],
          awaitRefetchQueries: true,
        });

        if (error) throw new Error(`GraphQL error: ${error.message}`);
        const deletedId = data?.deleteFromapp_usersCollection?.records?.[0]?.id;
        if (!deletedId) throw new Error("Failed to delete employee");
        return deletedId;
      },
      [mutate]
    );

    return useStableMutationResult(mutateAsync, result.loading);
  },
  useCreate: () => {
    const [mutate, result] = useMutation<CreateUsersMutation>(CreateUsersDocument);
    const mutateAsync = useCallback(
      async (input: App_UsersInsertInput) => {
        const {
          __typename: _typename,
          id: _id,
          ...cleanInput
        } = input as App_UsersInsertInput & {
          __typename?: unknown;
        };
        const { data, error } = await mutate({
          variables: { input: cleanInput },
          errorPolicy: "all",
          refetchQueries: ["GetUserss"],
          awaitRefetchQueries: true,
        });
        if (error) throw new Error(`GraphQL error: ${error.message}`);
        const created = data?.insertIntoapp_usersCollection?.records?.[0];
        if (!created) throw new Error("Failed to create employee");
        return created as App_Users;
      },
      [mutate]
    );
    return useStableMutationResult(mutateAsync, result.loading);
  },
};

export const useEmployeeHooks = (): EntityHooks<App_Users> => EMPLOYEE_ENTITY_HOOKS;
